import MUIDataTable from 'mui-datatables'
import React, { useEffect } from 'react'
import { Typography, makeStyles, IconButton } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash'

import { useAuth } from 'src/modules/auth'
import { useActionRunner } from 'src/modules/notification'
import { Button, Container, FormikDialog } from 'src/modules/ui'

import { useAdmin } from '../hooks'
import { AddUsersModal, EditUsersModal } from './UsersModal'

const useStyles = makeStyles(theme => ({
  addButton: {
    marginLeft: theme.spacing(2),
  },
  columnButton: {
    margin: theme.spacing(0.5),
  },
}))

const Users = () => {
  const classes = useStyles()
  const {
    fetchRoles,
    roles,
    fetchPurchasingEnvironments,
    purchasingEnvironments,
    fetchUsers,
    deleteUsers,
    restoreUsers,
    users,
  } = useAdmin()
  const { user: currentUser } = useAuth()

  const [fetchData, isFetching] = useActionRunner(
    [fetchUsers, fetchRoles, fetchPurchasingEnvironments],
    { initialFetchingState: true }
  )

  useEffect(() => {
    fetchData()
  }, [])

  const userData = isFetching
    ? []
    : users.map(
        ({ firstName, lastName, role, environments, deletedAt, ...user }) => ({
          ...user,
          name: `${firstName} ${lastName} `,
          role: role && roles.find(r => r.id === role)?.name,
          environments: environments.map(env => {
            return (
              Array.isArray(purchasingEnvironments) &&
              purchasingEnvironments.find(pEnv => pEnv.id === env).name
            )
          }),
          active: deletedAt ? 'Deleted users' : 'Active users',
        })
      )

  // datatable columns
  const columns = [
    { name: 'name', label: 'Name', options: { filter: false, sort: true } },
    { name: 'email', label: 'Email', options: { filter: false, sort: true } },
    {
      name: 'jobTitle',
      label: 'Job Title',
      options: { filter: false, sort: true },
    },
    { name: 'role', label: 'Role', options: { filter: true } },
    {
      name: 'id',
      label: ' ',
      options: {
        filter: false,
        sort: true,
        customBodyRender: userId => {
          const user = users.find(u => u.id === userId)
          return user.id === currentUser.id ? (
            <>
              <EditUsersModal
                user={user}
                trigger={props => (
                  <IconButton
                    {...props}
                    color="primary"
                    size="small"
                    className={classes.columnButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              />
            </>
          ) : !user.deletedAt ? (
            <>
              <EditUsersModal
                user={user}
                trigger={props => (
                  <IconButton
                    {...props}
                    color="primary"
                    size="small"
                    className={classes.columnButton}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              />
              <FormikDialog
                successNotification={{
                  image: 'carrots',
                  message: 'User deleted',
                }}
                onSubmit={() => deleteUsers(userId)}
                submitColor="darkRed"
                submitText="Delete User"
                trigger={props => (
                  <IconButton
                    size="small"
                    color="primary"
                    className={classes.columnButton}
                    {...props}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              >
                <Typography>
                  Are you sure you want to delete this user?
                </Typography>
              </FormikDialog>
            </>
          ) : (
            <FormikDialog
              successNotification={{
                image: 'carrots',
                message: 'User restored!',
              }}
              onSubmit={() => restoreUsers(userId)}
              submitText="Restore"
              trigger={props => (
                <IconButton
                  size="small"
                  color="primary"
                  className={classes.columnButton}
                  {...props}
                >
                  <RestoreFromTrashIcon />
                </IconButton>
              )}
            >
              <Typography>Restore user?</Typography>
            </FormikDialog>
          )
        },
      },
    },
    {
      name: 'environments',
      label: 'Environments',
      options: {
        display: 'excluded',
        filter: true,
        filterType: 'multiselect',
      },
    },
    {
      name: 'active',
      label: 'Active?',
      options: {
        display: 'excluded',
        filter: true,
        filterType: 'dropdown',
        filterList: ['Active users'],
      },
    },
  ]
  //  datatable options
  const options = {
    download: false,
    elevation: 0,
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    rowHover: false,
    selectableRows: 'none',
    viewColumns: false,
    customToolbar: props => (
      <AddUsersModal
        trigger={props => (
          <Button {...props} color="lightGreen" className={classes.addButton}>
            Add
          </Button>
        )}
      />
    ),
  }

  return (
    <Container muiTable>
      <MUIDataTable
        title={'Users'}
        data={userData}
        columns={columns}
        options={options}
      />
    </Container>
  )
}

export default Users
